'use client'

import type { TextImageBlock as TextImageBlockProps } from 'src/payload-types'

import React from 'react'
import { motion } from 'motion/react'
import { useInView } from 'react-intersection-observer'
import AnimatedText from '@/components/Antonie/Components/animated-text'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import ParallaxImage from '@/components/Antonie/Components/ParallaxImage'
import { getAltTag } from '@/utilities/getAltTag'
import Link from 'next/link'
import { getLink } from '@/utilities/getLink'
import RichText from '@/components/RichText'

type TextImageItemProps = {
  item: any
  index: number
}

const TextImageItem: React.FC<TextImageItemProps> = ({ item, index }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
    },
  }

  return (
    <motion.div
      ref={ref}
      variants={containerVariants}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      className={`relative flex mb-32 last:mb-0 ${item.hasLink ? 'md:ml-16' : 'md:mr-16'} ${
        index % 2 === 0 ? 'md:flex-row-reverse' : 'md:flex-row'
      }`}
    >
      <div
        className={`flex flex-col gap-12 md:flex-row ${index % 2 === 0 ? 'md:flex-row-reverse' : 'md:flex-row'}`}
      >
        <motion.div variants={itemVariants} className={`relative w-full`}>
          <div className="aspect-4/3 group relative overflow-hidden">
            <ParallaxImage
              src={getMediaUrl(item.image)}
              alt={getAltTag(item.image, item.title)}
              className="transition-transform duration-700 group-hover:scale-105"
            />
            <div className="bg-linear-to-t absolute inset-0 from-black/40 via-black/20 to-transparent transition-opacity duration-300 group-hover:opacity-70" />
            <div className="absolute -right-4 -top-4 h-24 w-24 bg-antonie-red/10 blur-2xl transition-all duration-300 group-hover:scale-150" />
            <div className="absolute -bottom-4 -left-4 h-24 w-24 bg-antonie-red/10 blur-2xl transition-all duration-300 group-hover:scale-150" />
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className={`flex w-full flex-col justify-center`}>
          <div className="relative">
            {item.title && (
              <AnimatedText
                text={item.title}
                type="h3"
                variant="simple"
                className="mb-6 font-heading text-4xl font-medium text-gray-900 md:text-4xl"
              />
            )}
            {item.description && (
              <RichText data={item.description} enableGutter={false} enableProse={true} />
            )}
            {item.hasLink && (
              <Link href={getLink(item.link)} target={item.link?.newTab ? '_blank' : '_self'}>
                <button className="group relative inline-flex items-center overflow-hidden border-2 border-antonie-green px-8 py-3 text-base font-light text-black transition-all duration-300 hover:bg-antonie-green hover:text-black">
                  <span className="relative z-10">{item.link.label}</span>
                  <div className="absolute inset-0 -z-10 h-full w-full scale-x-0 transform bg-antonie-green transition-transform duration-300 group-hover:scale-x-100" />
                </button>
              </Link>
            )}
          </div>
        </motion.div>
      </div>
    </motion.div>
  )
}

type Props = {
  className?: string
} & TextImageBlockProps

export const TextImageBlock: React.FC<Props> = ({ title, items }) => {
  return (
    <section className="relative overflow-hidden bg-stone-50 py-12 md:py-24">
      {title && (
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <AnimatedText
            text={title}
            type="h1"
            variant="simple"
            className="mb-6 font-heading text-2xl font-medium tracking-wide text-gray-900 md:text-4xl"
          />
          <div className="mx-auto mb-8 h-px w-16 bg-antonie-red" />
        </div>
      )}
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {items.map((item: any, index: number) => (
          <TextImageItem key={index} item={item} index={index} />
        ))}
      </div>
    </section>
  )
}
